import { useAuth } from '../hooks/useAuth'
import { useParams, useNavigate } from 'react-router-dom'
import { useEffect, useState } from 'react'
import Navigation from '../components/Navigation'
import { apiClient, type MatchDetails } from '../services/apiClient'

export default function MatchDetailsPage() {
  const { user } = useAuth()
  const { matchId } = useParams<{ matchId: string }>()
  const navigate = useNavigate()
  const [matchDetails, setMatchDetails] = useState<MatchDetails | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null)

  useEffect(() => {
    const fetchMatchDetails = async () => {
      if (!matchId) {
        setError('No match ID provided')
        setLoading(false)
        return
      }

      try {
        setLoading(true)
        setError(null)
        
        const details = await apiClient.getMatchDetails(matchId)
        setMatchDetails(details)
      } catch (err) {
        console.error('Error fetching match details:', err)
        setError(err instanceof Error ? err.message : 'Failed to load match details')
      } finally {
        setLoading(false)
      }
    }

    if (user && matchId) {
      fetchMatchDetails()
    }
  }, [user, matchId])

  if (!user) {
    return (
      <div className="min-h-screen bg-firedup-dark flex items-center justify-center">
        <div className="text-firedup-orange text-xl">Redirecting...</div>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-firedup-dark">
        <Navigation />
        <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="text-firedup-orange text-xl">Loading match details...</div>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-firedup-dark">
        <Navigation />
        <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="text-red-500 text-xl mb-4">Error loading match details</div>
            <div className="text-gray-300 mb-6">{error}</div>
            <button
              onClick={() => navigate('/dashboard')}
              className="bg-firedup-orange hover:bg-firedup-orange/80 text-white font-bold py-2 px-4 rounded"
            >
              Return to Dashboard
            </button>
          </div>
        </div>
      </div>
    )
  }

  if (!matchDetails) {
    return (
      <div className="min-h-screen bg-firedup-dark">
        <Navigation />
        <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="text-gray-400 text-xl">No match details found</div>
          </div>
        </div>
      </div>
    )
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return {
      date: date.toLocaleDateString(),
      time: date.toLocaleTimeString()
    }
  }

  const formatDuration = (seconds: number) => {
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
  }

  const handleSort = (key: string) => {
    let direction: 'asc' | 'desc' = 'asc'
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc'
    }
    setSortConfig({ key, direction })
  }

  const sortData = (data: any[], key: string, direction: 'asc' | 'desc') => {
    return [...data].sort((a, b) => {
      const aValue = a[key]
      const bValue = b[key]
      
      // Handle different data types
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return direction === 'asc' ? aValue - bValue : bValue - aValue
      }
      
      const aString = String(aValue).toLowerCase()
      const bString = String(bValue).toLowerCase()
      
      if (direction === 'asc') {
        return aString < bString ? -1 : aString > bString ? 1 : 0
      } else {
        return aString > bString ? -1 : aString < bString ? 1 : 0
      }
    })
  }

  const { date, time } = formatDate(matchDetails.playedAt)

  return (
    <div className="min-h-screen bg-firedup-dark">
      <Navigation />
      
      <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <button
            onClick={() => navigate('/dashboard')}
            className="text-firedup-orange hover:text-firedup-orange/80 mb-4 flex items-center gap-2"
          >
            ← Back to Dashboard
          </button>
          <h1 className="text-4xl font-black text-white mb-2">
            Match Details: <span className="text-firedup-orange">{matchDetails.gameName}</span>
          </h1>
          <p className="text-xl text-gray-300">
            Detailed stats from your claimed match
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          <div className="bg-firedup-gray/50 backdrop-blur-sm p-6 rounded-lg border border-firedup-light-gray">
            <h3 className="text-xl font-bold text-white mb-4">🎮 Match Information</h3>
            
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-400">Location:</span>
                <span className="text-white">{matchDetails.location}</span>
              </div>
              
              <div className="flex justify-between">
                <span className="text-gray-400">Date:</span>
                <span className="text-white">{date}</span>
              </div>
              
              <div className="flex justify-between">
                <span className="text-gray-400">Time:</span>
                <span className="text-white">{time}</span>
              </div>

              {matchDetails.matchData.durationSeconds && (
                <div className="flex justify-between">
                  <span className="text-gray-400">Duration:</span>
                  <span className="text-white">{formatDuration(matchDetails.matchData.durationSeconds)}</span>
                </div>
              )}
            </div>
          </div>

          <div className="bg-firedup-gray/50 backdrop-blur-sm p-6 rounded-lg border border-firedup-light-gray flex items-center justify-center">
            <div className="text-center">
              <div className="text-5xl font-bold text-firedup-orange mb-2">
                {Math.round(matchDetails.playerStats.score).toLocaleString()}
              </div>
              <div className="text-gray-400">Your Score</div>
            </div>
          </div>
        </div>

        {matchDetails.matchData && Object.keys(matchDetails.matchData).length > 0 && (
          <div className="bg-firedup-gray/50 backdrop-blur-sm p-6 rounded-lg border border-firedup-light-gray">
            <h3 className="text-xl font-bold text-white mb-6">📋 Match Player Data</h3>
            
            {(() => {
              const findPlayerArrays = (obj: any, path: string = ''): Array<[string, any[]]> => {
                const results: Array<[string, any[]]> = [];
                
                Object.entries(obj).forEach(([key, value]) => {
                  const currentPath = path ? `${path}.${key}` : key;
                  
                  if (['durationSeconds', 'uniqueMatchId', 'matchId'].includes(key)) {
                    return;
                  }
                  
                  if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'object') {
                    results.push([currentPath, value]);
                  } else if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
                    results.push(...findPlayerArrays(value, currentPath));
                  }
                });
                
                return results;
              };
              
              const playerArrays = findPlayerArrays(matchDetails.matchData);
              
              if (playerArrays.length > 0) {
                return playerArrays.map(([sectionKey, players]) => {
                  return (
                    <div key={sectionKey} className="mb-6">
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-firedup-light-gray/30">
                              {Object.keys((players as any[])[0]).filter(key => key.toLowerCase() !== 'id').map(key => (
                                <th 
                                  key={key} 
                                  className="text-left py-3 px-4 text-gray-300 font-medium capitalize cursor-pointer hover:text-firedup-orange transition-colors"
                                  onClick={() => handleSort(key)}
                                >
                                  <div className="flex items-center gap-1">
                                    {key.replace(/([A-Z])/g, ' $1').trim()}
                                    {sortConfig?.key === key && (
                                      <span className="text-firedup-orange">
                                        {sortConfig.direction === 'asc' ? '↑' : '↓'}
                                      </span>
                                    )}
                                  </div>
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {(() => {
                              const sortedPlayers = sortConfig 
                                ? sortData(players as any[], sortConfig.key, sortConfig.direction)
                                : players as any[];
                              
                              return sortedPlayers.map((player: any, index: number) => (
                                <tr key={index} className="border-b border-firedup-light-gray/20 hover:bg-firedup-dark/30">
                                  {Object.entries(player).filter(([key]) => key.toLowerCase() !== 'id').map(([key, value]) => (
                                    <td key={key} className="py-3 px-4 text-white">
                                      {typeof value === 'number' && key.toLowerCase().includes('score') ? (
                                        <span className="font-semibold text-firedup-orange">
                                          {value.toLocaleString()}
                                        </span>
                                      ) : typeof value === 'number' && key.toLowerCase() === 'accuracy' && matchDetails.gameName.toLowerCase().includes('hunter vr') ? (
                                        <span className="text-firedup-orange">
                                          {(value * 100).toFixed(1)}%
                                        </span>
                                      ) : typeof value === 'object' ? (
                                        <span className="text-xs text-gray-400">
                                          {JSON.stringify(value)}
                                        </span>
                                      ) : (
                                        String(value)
                                      )}
                                    </td>
                                  ))}
                                </tr>
                              ));
                            })()}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                });
              } else {
                const flattenData = (obj: any, prefix = ''): Array<[string, any]> => {
                  const results: Array<[string, any]> = [];
                  Object.entries(obj).forEach(([key, value]) => {
                    if (['durationSeconds', 'uniqueMatchId', 'matchId'].includes(key)) {
                      return;
                    }
                    const newKey = prefix ? `${prefix}.${key}` : key;
                    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
                      results.push(...flattenData(value, newKey));
                    } else {
                      results.push([newKey, value]);
                    }
                  });
                  return results;
                };
                
                const flatData = flattenData(matchDetails.matchData);
                
                return (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {flatData.map(([key, value]) => (
                      <div key={key} className="flex justify-between py-2 border-b border-firedup-light-gray/30">
                        <span className="text-gray-400 capitalize">
                          {key.replace(/([A-Z])/g, ' $1').trim()}:
                        </span>
                        <span className="text-white">
                          {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                        </span>
                      </div>
                    ))}
                  </div>
                );
              }
            })()}
          </div>
        )}
      </div>
    </div>
  )
}
